import React, { useState, useContext, useRef, useCallback } from 'react';
import { Constants } from '../Common/Constants';
import { calculateTax, stringIsNullOrEmpty, zeroIfNull, formatNumber } from '../Common/Utilities';
import { STATUS_ATTRIBUTE, VIEW_ATTRIBUTE } from './InvoiceDocumentEditorFunctions';
import { toast } from 'react-toastify';


const InvoiceContext = React.createContext();

export const InvoiceDocumentEditorContext = ({ children, ...props }) => {

	const [state, setState] = useState({
		isDirty: false,
		lineDirty: false,
		readOnly: true,
		isAttachementEnable: false,
		source: props.info.source || props.info.rowData?.invoiceSource || '',
		mode: props.info.mode || "View",
		title: '',
		openVendorDialog: false,
		invoiceDocument:null,
		openClientDialog: false,
		openProductDialog: false,
		openPOHeaderDialog: false,
		openLevel2Dialog: false,
		openLevel3Dialog: false,
		openCostCodesDialog: false,
		openPOHeaderResourceDialog: false,
		openLevel2ResourceDialog: false,
		openChangeVendorConfirmation: false,
		openReplaceInvoiceDocumentEditorDialog: false,
		openInvoiceDocumentRoutingHistoryDialog: false,
		openReplaceFileScanInProgress: (props.info.mode === "Replace" && true) || false,
		vendor: null,
		inProgress: false,
		replacedInvoiceDocument: null,
		serviceTermStartDate: null,
		serviceTermEndDate: null,
		isInvoiceNumberDuplicate: false,
		uncollapsed: true,
		displayInvoiceDocumentViewer: "",
		openDeleteConfirmation: false,
		deleteRowData: null,
		isGridDataValid: false,
		invalidRowsList: new Set(),
		invalidRowsMessage: {},
		taxes: [],
		originalInvoiceDocumentStatus: 0,
		originalInvoiceStatusIdForGadgetLoading: 0,
		routingHistoryData: [],
		userRoleName: '',
		source: '',
		invoiceDocumentImage: null,
		uiViewAttribute: VIEW_ATTRIBUTE.READ_ONLY,
		uiStatusAttribute: STATUS_ATTRIBUTE.NORMAL,
		headerTaxCode: '',
		capabilities: {
			canPostToERP: false,
			canRepostToERP: false,
			canEditInvoice: false,
			canOverridInvoice: false,
			canRepost: false,
			hasDifferentCompany: false,
			canDelete: false,
			canPostCreditMemo:false
		},
		partialInvoiceStatus: props?.info?.repostInfo,
		updatedRowFields: null,
		ioSessionMode: null,
	});
	const [partialPostSeqIdsMap, setPartialPostSeqIdsMap] = useState(null);
	const [title, setTitle] = useState(null);

	const [dialog, setDialog] = React.useState({
		showPartialPostDialog:false
	})

	const [successfullSave, setSuccessfullSave] = React.useState(false);

	const isFirstRenderRef = useRef(true);
	const falseCountRef = useRef(0);

	// #region Validation Methods

	const groupAndSum = (arr, groupKeys, sumKeys) => {
		return Object.values(
			arr.reduce((acc, curr) => {
				const group = groupKeys.map(k => curr[k]).join('-');
				acc[group] = acc[group] || Object.fromEntries(groupKeys.map(k => [k, curr[k]]).concat(sumKeys.map(k => [k, 0])));
				sumKeys.forEach(k => acc[group][k] += parseFloat(curr[k]));
				return acc;
			}, {})
		);
	}

	const TaxCodeValid = (newData) => {
		var isValid = true;
		const invoiceDocument = state.invoiceDocument;
		if (invoiceDocument && invoiceDocument.invoiceDocumentEditorDetails) {
			var IOPO = invoiceDocument.invoiceDocumentEditorDetails;
			var invoiceDocumentEditorDetails = IOPO.filter(x => x.ioNumber && x.ioNumber == newData.ioNumber && x.taxCode != newData.taxCode);
			if (invoiceDocumentEditorDetails && invoiceDocumentEditorDetails.length > 0) {
				isValid = false;
			}
		}
		return isValid;
	}

	const updateTaxCode = (newData, invoiceDocument) => {
		let selectedTaxCode = "";
		if (isFirstRenderRef.current === false && falseCountRef.current === 0) {
			falseCountRef.current += 1;
			return;
		}
		var IOPO = invoiceDocument.invoiceDocumentEditorDetails;
		var isNew = IOPO.some(x => x.isNew === true);
		if (isNew)
			return;
		var invoiceDocumentEditorDetails = IOPO.filter(x => x.ioNumber == newData.ioNumber && x.taxCode != newData.taxCode && x.hasAuthority === 'Y' && (x.allowInvoiceCorrection === 'Y' || x.allowInvoiceEditing === true));
		var getData = newData.hasAuthority === 'Y' && (newData.allowInvoiceCorrection === 'Y' || newData.allowInvoiceEditing === true)
		
		if (getData) {
			selectedTaxCode = newData.taxCode;
		} else {
			selectedTaxCode = invoiceDocumentEditorDetails.length > 0 ? invoiceDocumentEditorDetails[0].taxCode : "";
		}
		
		for (const item of invoiceDocumentEditorDetails) {
			item.taxCode = selectedTaxCode;
			// Use the full (non-deduped) tax list from the invoice document so taxTypeCode is
			// preserved. state.taxes is a taxCode-only lookup (deduped for dropdown display) and
			// does not carry taxTypeCode, which previously caused a blank tax_type_code to be
			// saved for sibling lines sharing the same IO/tax code.
			calculateTaxAmountForRow(item.taxCode, item, invoiceDocument.taxes || []);
		}
		setState(prev => ({ ...prev, isDirty: true, invoiceDocument: { ...invoiceDocument, invoiceDocumentEditorDetails: [...invoiceDocument.invoiceDocumentEditorDetails] } }));
	}

	const InvoiceAmountValid = (rowData) => {
		var isValid = false;
		if (state.invoiceDocument.modelDefId === 2) {
			isValid = rowData.ioInvoiceAmount != 0;
			if (isValid) {
				if (!stringIsNullOrEmpty(state.invoiceDocument?.poCode)) {
					var invoiceDocumentEditorDetails = JSON.parse(JSON.stringify(state.invoiceDocument.invoiceDocumentEditorDetails));
					var index = invoiceDocumentEditorDetails.findIndex(d => d.seqId === rowData.seqId);
					if (index < 0) {
						invoiceDocumentEditorDetails.push(JSON.parse(JSON.stringify(rowData)));
					} else {
						invoiceDocumentEditorDetails[index] = JSON.parse(JSON.stringify(rowData));
					}
					invoiceDocumentEditorDetails[invoiceDocumentEditorDetails.findIndex(d => d.seqId === rowData.seqId)].ioInvoiceAmount = Number(Number(rowData.ioInvoiceAmount).toFixed(2));
					var groupedInvoiceDocumentDetails = groupAndSum(invoiceDocumentEditorDetails, ['level2Key', 'tolerancePOFlag', 'tolerancePOAmount', 'level3Key', 'resType', 'remainingAmount'], ['ioInvoiceAmount']);
					var groupedFilteredOnKeyFields = groupedInvoiceDocumentDetails.filter(d => d.level2Key === rowData.level2Key && d.level3Key === rowData.level3Key && d.resType === rowData.resType);
					isValid = !(groupedFilteredOnKeyFields.some(d => {
						var isInvoiceAmountInvalid = Number(zeroIfNull(parseFloat(d.ioInvoiceAmount || 0)).toFixed(2)) > Number(zeroIfNull(d.remainingAmount).toFixed(2));
						if (isInvoiceAmountInvalid) {
							if (d.tolerancePOFlag === 'N') {
								isInvoiceAmountInvalid = Number(zeroIfNull(parseFloat(d.ioInvoiceAmount || 0)).toFixed(2)) > Number((zeroIfNull(d.remainingAmount) + zeroIfNull(d.tolerancePOAmount)).toFixed(2));
							}
							else {
								var percentRemainingAmount = (zeroIfNull(d.tolerancePOAmount) * zeroIfNull(d.remainingAmount)) / 100.00;
								isInvoiceAmountInvalid = Number(Number(parseFloat(d.ioInvoiceAmount || 0)).toFixed(2)) > Number((d.remainingAmount + percentRemainingAmount).toFixed(2));
							}
						}
						return isInvoiceAmountInvalid;
					}));
				}
			}
		}
		else {
			isValid = rowData.ioInvoiceAmount != 0;
			if (isValid) {
				var invoiceDocumentEditorDetails = JSON.parse(JSON.stringify(state.invoiceDocument.invoiceDocumentEditorDetails));
				var index = invoiceDocumentEditorDetails.findIndex(d => d.seqId === rowData.seqId);
				if (index < 0) {
					invoiceDocumentEditorDetails.push(JSON.parse(JSON.stringify(rowData)));
				} else {
					invoiceDocumentEditorDetails[index] = JSON.parse(JSON.stringify(rowData));
				}
				invoiceDocumentEditorDetails[invoiceDocumentEditorDetails.findIndex(d => d.seqId === rowData.seqId)].ioInvoiceAmount = Number(Number(rowData.ioInvoiceAmount).toFixed(2));
				var groupedInvoiceDocumentDetails = groupAndSum(invoiceDocumentEditorDetails, ['ioNumber', 'poAmount', 'consumedAmount', 'tolerancePOFlag', 'tolerancePOAmount'], ['ioInvoiceAmount']);
				var groupedFilteredOnKeyFields = groupedInvoiceDocumentDetails.filter(d => d.ioNumber === rowData.ioNumber);

				isValid = !(groupedFilteredOnKeyFields.some(d => {
					var remainingAmount = (Number(zeroIfNull(d.poAmount).toFixed(2)) - Number(zeroIfNull(d.consumedAmount).toFixed(2)));
					var isInvoiceAmountInvalid = Number(Number(zeroIfNull(parseFloat(d.ioInvoiceAmount || 0))).toFixed(2)) > Number(remainingAmount.toFixed(2));
					if (isInvoiceAmountInvalid) {
						if (d.tolerancePOFlag === 'N') {
							var remainingWithTol = Number((zeroIfNull(remainingAmount) + zeroIfNull(d.tolerancePOAmount)).toFixed(2));
							if (remainingWithTol < 0)
								remainingWithTol = 0
							isInvoiceAmountInvalid = Number(zeroIfNull(parseFloat(d.ioInvoiceAmount || 0)).toFixed(2)) > Number((remainingWithTol).toFixed(2));
						}
						else {
							var percentRemainingAmount = (zeroIfNull(d.tolerancePOAmount) * zeroIfNull(d.poAmount)) / 100.00;
							var remainingWithTol = Number((zeroIfNull(remainingAmount) + percentRemainingAmount).toFixed(2));
							if (remainingWithTol < 0)
								remainingWithTol = 0
							isInvoiceAmountInvalid = Number(Number(d.ioInvoiceAmount).toFixed(2)) > Number((remainingWithTol).toFixed(2));
						}
					}
					return isInvoiceAmountInvalid;
				}));
			}
		}
		return isValid;
	}

	const validateIfNotEmpty = React.useCallback((row) => {
		let isValid = false;
		if (state.invoiceDocument?.modelDefId !== 1) {
			isValid = parseInt(row.quantity || '0', 10) !== 0 ||
				parseFloat(row.ioInvoiceAmount || '0') !== 0 ||
				row.taxCode
				|| row.level2Key
				|| row.level3Key
				|| row.rTypeName
				|| row.ioDescription?.trim()
		} else {
			isValid = parseFloat(row.ioInvoiceAmount || '0') !== 0 ||
				row.ioNumber ||
				row.taxCode ||
				parseFloat(row.remainingAmount || '0') !== 0 ||
				parseFloat(row.netCost || '0') !== 0 ||
				row.ioDescription?.trim()
		}
		return isValid
	});

	const validation = React.useCallback((row, notify, sender) => {
		let isValid = false;
		let msg = '';
		let type = '';
		if (state.invoiceDocument?.modelDefId !== 2) {
			var isTaxCodeValid = TaxCodeValid(row);
			if (InvoiceAmountValid(row)) {
				var invoiceDocument = state.invoiceDocument;
				row.quantity = isNaN(parseInt(row.quantity, 10)) ? row.quantity : parseInt(row.quantity, 10);
				row.ioInvoiceAmount = isNaN(parseInt(row.ioInvoiceAmount, 10)) ? row.ioInvoiceAmount : parseFloat(row.ioInvoiceAmount);
				if (!isTaxCodeValid) {
					{ notify && validateIfNotEmpty(row) && toast.success("All IO Line(s) with same PO Code updated with new Tax."); }
					{ !isFirstRenderRef.current && updateTaxCode(sender || row, invoiceDocument); }
				}
				isValid = true;
			}
			else {
				msg = 'Invoice Net Amount should be less than or equal to remaining amount: ' + formatNumber(row.allocatedAmount);
				type = '1';
				{ notify && validateIfNotEmpty(row) && toast.error(msg); }
			}
		}
		else {
			if (row.ioInvoiceAmount !== 0) {
				if (InvoiceAmountValid(row)) {
					var invoiceDocument = state.invoiceDocument;
					row.costType = zeroIfNull(parseInt(row.costType, 10));
					row.resType = zeroIfNull(parseInt(row.resType, 10));
					row.quantity = isNaN(zeroIfNull(parseInt(row.quantity, 10))) ? row.quantity : zeroIfNull(parseInt(row.quantity, 10));
					row.ioInvoiceAmount = isNaN(parseFloat(row.ioInvoiceAmount)) ? row.ioInvoiceAmount : parseFloat(row.ioInvoiceAmount);
					isValid = true;
				}
				else {
					type = '1';
					{
						msg = 'Invoice Amount should be less than or equal to remaining amount';
						notify && validateIfNotEmpty(row) && toast.error(msg);
					}
				}
				if (isValid) {
					if (row.quantity <= 0) {
						type = '2'
						msg = 'Invoice Quantity should be greater than 0';
						{
							notify && validateIfNotEmpty(row) && toast.error(msg)
						}
						isValid = false;
					}
				}
			}
			else {
				msg = 'Invoice Amount should be greater than 0';
				type = '1';
				{
					notify && validateIfNotEmpty(row) && toast.error(msg)
				}
			}
		}
		return {
			isValid: isValid, details: { errorMsg: msg, type: type }
		};
	});

	const triggerValidation = React.useCallback((invoiceDocumentEditorDetails, notify = false, sender = null) => {
		if (state.invoiceDocument?.modelDefId === 3) return;
		let details = (invoiceDocumentEditorDetails || state.invoiceDocument?.invoiceDocumentEditorDetails || []).filter((e) => validateIfNotEmpty(e));
		let invalidRows = [];
		let message = {};
		details.forEach(e => {
			if (validateIfNotEmpty(e)) {
				let validationResponse = validation(e, notify, sender);
				if (validationResponse.isValid) {
					return true;
				}
				else {
					invalidRows.push(e.seqId);
					message[e.seqId] = validationResponse.details;
					return false;
				}
			}
		});
		isFirstRenderRef.current = false;
		let someValid = invalidRows.length == 0 && details.length !== 0;
		setState(prev => ({
			...prev,
			isGridDataValid: someValid,
			invalidRowsList: new Set([...invalidRows]),
			invalidRowsMessage: message
		}));
		return someValid;
	});

	// #region methods
	const setDetailsOnVendorChange = React.useCallback(() => {
		const details = state.invoiceDocument?.invoiceDocumentEditorDetails?.map(function (detail, index) {
			detail.ioNumber = "";
			detail.poAmount = 0.00;
			detail.consumedAmount = 0.00;
			detail.deliveryAmount = 0.00;
			detail.clientCode = "";
			detail.taxCode = "";
			detail.taxAmount = 0.00;
			detail.ioDescription = "";
			detail.mediaType = "";
			detail.siteName = "";
			return detail;
		});
		setState({ ...state, invoiceDocument: { ...state.invoiceDocument, invoiceDocumentEditorDetails: details } })
	})

	const getCalculatedTax = (taxeList,taxCode,row) => {
		var taxes = taxeList.filter(t => t.taxCode === taxCode);
		let totalTax = 0.00;
		taxes.forEach(tax => {
			let calculatedTaxAmount =  calculateTax(tax, row.ioInvoiceAmount);

			calculatedTaxAmount = parseFloat(calculatedTaxAmount.toFixed(2));
			totalTax += calculatedTaxAmount
		});
		return totalTax;
	}

	const calculateTaxAmountForRow = (taxCode, row, taxesList) => {
		let originalAmount = 0;

		try {
			if (row.taxAmount && !isNaN(row.taxAmount))
				originalAmount = parseFloat(row.taxAmount);
		} catch (ex) { }

		row.taxAmount = 0.00;
		row.invoiceDocumentDetailTaxes = [];

		if (!taxCode) return row;

		const taxes = taxesList.filter(t => t.taxCode === taxCode);

		let totalTaxBaseOnIoAmount = taxes
			.map((e) => calculateTax(e, row.ioInvoiceAmount))
			.reduce((prev, next) => prev + next, 0);

		taxes.forEach(tax => {
			let calculatedTaxAmount =
				row.isTaxManuallyChanged
					? (row.ioInvoiceAmount === 0
						? 0
							: (originalAmount * calculateTax(tax, row.ioInvoiceAmount)) / totalTaxBaseOnIoAmount)
					: isNaN(row.ioInvoiceAmount)
						? 0.00
						: calculateTax(tax, row.ioInvoiceAmount);

			calculatedTaxAmount = parseFloat(calculatedTaxAmount.toFixed(2));

			row.invoiceDocumentDetailTaxes.push({
				companyCode: row.companyCode,
				invoiceDocumentId: row.invoiceDocumentId,
				seqId: row.seqId,
				taxCode: tax.taxCode,
				taxTypeCode: tax.taxTypeCode,
				taxPrc: tax.otherTaxAmt,
				taxAmount: calculatedTaxAmount
			});
		});

		if (row.isTaxManuallyChanged) {
			row.taxAmount = originalAmount;
			if (row.taxAmount === 0 && !taxCode) {
				row.invoiceDocumentDetailTaxes = [];
				row.taxCode = '';
			}
		} else {
			row.taxAmount = row.invoiceDocumentDetailTaxes
				.map(item => item.taxAmount)
				.reduce((prev, next) => prev + next, 0);

			row.taxAmount = parseFloat(row.taxAmount.toFixed(2));
		}

		return row;
	};

	const applyTaxCodeToAllLines = (newTaxCode) => {

		setState(prevState => {

			const taxesList = prevState.invoiceDocument?.taxes || [];
			const readOnlyRow = prevState.readOnly;
			let maxApprovalLevelBase = prevState.invoiceDocument?.companyApprovalConfiguration?.routingNumberOfApprovalLevels || 0;
			const maxApprovalStatus = (maxApprovalLevelBase * 20) + 50;

			const updatedDetails =
				prevState.invoiceDocument.invoiceDocumentEditorDetails.map(detail => {

					const lineStatus = Number(detail.lineStatus);

					const baseEditable =
						(detail.hasAuthority === 'Y' && detail.allowInvoiceCorrection === 'Y') ||
						lineStatus <= 50 ||
						lineStatus === 180 ||
						lineStatus == maxApprovalStatus || lineStatus == 170;

					const rowEnabled = !readOnlyRow && baseEditable;

					if (!rowEnabled) return detail;

					const updatedRow = {
						...detail,
						taxCode: newTaxCode,
						isTaxManuallyChanged: false
					};

					return calculateTaxAmountForRow(
						newTaxCode,
						updatedRow,
						taxesList
					);
				});

			return {
				...prevState,
				isDirty: true,
				invoiceDocument: {
					...prevState.invoiceDocument,
					invoiceDocumentEditorDetails: updatedDetails
				}
			};
		});
	};

	
	// #endregion methods

	return (<InvoiceContext.Provider value={{
		state: state, setState: setState,
		setDetailsOnVendorChange, getTitle, getCalculatedTax,
		successfullSave, setSuccessfullSave,
		dialog, setDialog, partialPostSeqIdsMap, setPartialPostSeqIdsMap,
		setTitle, title, applyTaxCodeToAllLines, triggerValidation, validateIfNotEmpty, calculateTaxAmountForRow

	}}>
		{children}
	</InvoiceContext.Provider>);
}

export const useInvoice = () => useContext(InvoiceContext);





const getTitle = (mode) => {
	var title;
	switch (mode) {
		case "View":
		case "EditDates":
			title = "View Invoice";
			break;
		case "PartialPost":
			title = "Invoice – Partial Posting";
			break;
		case "CreditMemo":
			title = "Credit Memo";
			break;
		case "Test":
			title = "View Invoice (Test)";
			break;
		case "UserEntry":
			title = "User Entry";
			break;
		case "ManualEntry":
			title = "Manual Entry";
			break;
		case "MakeCorrections":
			title = "Make Corrections";
			break;
		case "Replace":
			title = "Replace Invoice";
			break;
		default:
			title = "";
	}
	return title;
}